/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 * Widget Personal Account Component (case Success)
 *
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Misha Kokoulin
 * @author Aleksandr Perkhunov
 * @author Maxim Fedorov
 * @author Anton Panasenko
 * @author Alexander Golshtein
 * @author Evgeniy Naumov
 * @version 1.25.1
 * @since 1.0.1
 */

import React, { FC, Fragment, useCallback } from 'react';

import moment from 'moment';


import { icons } from '../../../../../../assets';
import { Button, LinkBlock } from '../../../../../../components/common';
import { FlexColumnStyled, FlexMiddleStyled } from '../../../../../../components/common/Block/Flexbox';
import { LayoutStyled } from '../../../../../../components/common/Block/Layout';
import { SeparateLineStyled } from '../../../../../../components/common/Block/SeparateLine';
import GTMOperations from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations';
import { EGTMWidgetPersonalAccountCloseType } from '../../../../../../modules/analytics/GoogleTagManager/GTMOperations/GTMWidgetPersonalAccountOperations';
import { appHistoryPush } from '../../../../../../modules/app/appHistory';
import { IModalRenderProps } from '../../../../../../modules/modal/types/modalTypes';
import PromisedPaymentModal from '../../../../../../modules/promisedPayment/components/PromisedPaymentModal';
import { CLIENT_ID, RoutePath } from '../../../../../../routes/constants/routesConstants';
import { declensionOfNumerals, formatBalance, formatDate } from '../../../../../../utils/formatText';
import { stringFormat } from '../../../../../../utils/stringFormat';
import { useCheckCustomRole } from '../../../../../../utils/useCheckCustomRole';
import { AuthorityRoleEnum, EAccountType } from '../../../../../PageAuth/constants/authConstants';
import { EFormatData } from '../../../../types/widgetTypes';
import { WidgetPersonalAccountBalanceDateStyled } from '../styled/WidgetPersonalAccountStyled';
import { IWidgetPersonalAccountSuccessProps } from '../types/widgetPersonalAccountType';
import WidgetPersonalAccountBalance from './WidgetPersonalAccountBalance';
import WidgetPersonalAccountCashback from './WidgetPersonalAccountCashback';
import WidgetPersonalAccountNotification from './WidgetPersonalAccountNotification';

const WidgetPersonalAccountSuccess: FC<IWidgetPersonalAccountSuccessProps> = (props) => {
    const {
        accountId,
        balanceDate,
        cashBack,
        cashbackUrl,
        creditLimit,
        currency,
        currentBalance,
        isCashbackV2Enabled,
        nearestPayDate,
        openMobileCash,
        outflowType,
        pushGtmAnalytic,
        scores,
        totalBalance,
        translation,
        unpaidBillsAmount,
        unpaidBillsQuantity,
        hasNewBills,
        showNewBillsNotification,
    } = props;

    const currentValue = currentBalance?.value ?? 0;
    const creditValue = creditLimit?.value ?? 0;

    const isCustomRoleCheckAvailable = useCheckCustomRole(true, AuthorityRoleEnum.ROLE_DOCS_MANAGE);

    const getUnpaidBillsUrl = (): string => {
        return RoutePath.billsUnpaidAccount.replace(CLIENT_ID, accountId.toString());
    };

    const openUnpaidBills = (event: React.MouseEvent): void => {
        event.preventDefault();
        const url = getUnpaidBillsUrl();

        appHistoryPush(url);
        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetPersonalAccount.clickLinkAmountUnpaidBillsWidgetPersonalAccount,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    const isCurrentBalanceDanger = (): boolean => {
        return outflowType === EAccountType.PREPAID || outflowType === EAccountType.EMPTY
            ? false
            : creditValue + currentValue <= Math.abs(currentValue * 0.1);
    };

    const promisedPaymentButtonHandler = useCallback(() => {
        props.openModal({
            render: (modalProps: IModalRenderProps) => (
                <PromisedPaymentModal clientId={accountId.toString()} modalId={modalProps.id} />
            ),
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const openMobileCashHandler = (): void => {
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetPersonalAccount.clickBtnBillsWidgetPersonalAccount,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
        openMobileCash({
            clientId: accountId.toString(),
            onApplyCallback: (prepaySum: string) => {
                // GTM Analytic
                props.pushGtmAnalytic({
                    operation: GTMOperations.widgetPersonalAccount.clickBtnMakeReportWidgetPersonalAccount(prepaySum),
                    withAccountType: true,
                    withSusrId: true,
                    withUserRole: true,
                });
            },
            onCloseCallback: (type: EGTMWidgetPersonalAccountCloseType) => {
                props.pushGtmAnalytic({
                    operation: GTMOperations.widgetPersonalAccount.clickBtnCloseMakeReportWidgetPersonalAccount(type),
                    withAccountType: true,
                    withSusrId: true,
                    withUserRole: true,
                });
            },
            onErrorLoadCallback: (error: string) => {
                props.pushGtmAnalytic({
                    operation: GTMOperations.widgetPersonalAccount.makeReportErrorWidgetPersonalAccount(error),
                    withAccountType: true,
                    withSusrId: true,
                    withUserRole: true,
                });
            },
            onSuccessLoadCallBack: (prepaySum: string) => {
                props.pushGtmAnalytic({
                    operation: GTMOperations.widgetPersonalAccount.makeReportSuccessWidgetPersonalAccount(prepaySum),
                    withAccountType: true,
                    withSusrId: true,
                    withUserRole: true,
                });
            },
        });
    };

    const getBillsUrl = (): string => {
        return RoutePath.billsAccount.replace(CLIENT_ID, accountId.toString());
    };

    const onClickShowBillsLink = (event: React.MouseEvent) => {
        event.preventDefault();
        const url = getBillsUrl();

        appHistoryPush(url);

        // GTM Analytic
        props.pushGtmAnalytic({
            operation: GTMOperations.widgetPersonalAccount.clickLinkShowBillingsWidgetPersonalAccount,
            withAccountType: true,
            withSusrId: true,
            withUserRole: true,
        });
    };

    return (
        <FlexColumnStyled className="widget-personal-account__success">
            {totalBalance && (
                <LayoutStyled className="mb-4 widget-personal-account__total-balance">
                    <WidgetPersonalAccountBalance
                        balance={totalBalance}
                        currency={currency}
                        name={translation.titleTotalBalance}
                    />

                    <WidgetPersonalAccountBalanceDateStyled>
                        {translation.on +
                            ' ' +
                            (balanceDate
                                ? formatDate(balanceDate)
                                : formatDate(moment().format(EFormatData.FullDataWithTimeZone)))}
                    </WidgetPersonalAccountBalanceDateStyled>
                </LayoutStyled>
            )}

            <LayoutStyled className="mb-4 widget-personal-account__current-balance">
                <WidgetPersonalAccountBalance
                    balance={currentBalance}
                    currency={currency}
                    isBalanceDanger={isCurrentBalanceDanger()}
                    name={translation.titleCurrentBalance}
                />

                <WidgetPersonalAccountBalanceDateStyled>
                    {translation.on +
                        ' ' +
                        (balanceDate
                            ? formatDate(balanceDate)
                            : formatDate(moment().format(EFormatData.FullDataWithTimeZone)))}
                </WidgetPersonalAccountBalanceDateStyled>
            </LayoutStyled>

            {creditLimit && !!creditLimit.value && (
                <FlexColumnStyled className="widget-personal-account__credit-limit mb-4">
                    <WidgetPersonalAccountBalance
                        balance={creditLimit}
                        currency={currency}
                        isCredit={true}
                        name={translation.titleCreditLimit}
                    />
                </FlexColumnStyled>
            )}

            {!!props.activePromisedPaymentAmount && (
                <LayoutStyled className="mb-4 widget-personal-account__promised-payment">
                    <WidgetPersonalAccountBalance
                        balance={{
                            value: props.activePromisedPaymentAmount,
                        }}
                        currency={currency}
                        name={translation.promisedPayment.title}
                    />
                    {!!props.activePromisedPaymentEndDate && (
                        <WidgetPersonalAccountBalanceDateStyled>
                            {translation.till + ' ' + formatDate(props.activePromisedPaymentEndDate)}
                        </WidgetPersonalAccountBalanceDateStyled>
                    )}
                </LayoutStyled>
            )}

            {cashBack && (
                <LayoutStyled className="mb-4">
                    <WidgetPersonalAccountCashback
                        balance={cashBack}
                        cashbackUrl={cashbackUrl}
                        isCashbackV2Enabled={isCashbackV2Enabled}
                        pushGtmAnalytic={pushGtmAnalytic}
                        scores={scores}
                        translation={translation}
                    />
                </LayoutStyled>
            )}

            <LayoutStyled className="widget-personal-account__credit-notification">
                <SeparateLineStyled className="mb-4" />

                {!!unpaidBillsAmount
                    && !!unpaidBillsQuantity
                    && isCustomRoleCheckAvailable
                    && (
                    <WidgetPersonalAccountNotification
                        className={`${nearestPayDate ? 'mb-3' : 'mb-4'}`}
                        data-btn="openUnpaidBills"
                        icon={icons.balanceMediumIcon}
                        text={stringFormat(
                            translation.notificationUnpaidBills,
                            unpaidBillsQuantity,
                            declensionOfNumerals(unpaidBillsQuantity, translation.unpaidBillsQuantity),
                            `${formatBalance(unpaidBillsAmount)} ${currency}`,
                        )}
                    >
                        <LinkBlock
                            className=" unpaid-link_amount"
                            dataName="openUnpaidBills"
                            url={getUnpaidBillsUrl()}
                            onClick={openUnpaidBills}
                        >
                            {translation.linkShow}
                        </LinkBlock>
                    </WidgetPersonalAccountNotification>
                )}
                {!!nearestPayDate
                    && isCustomRoleCheckAvailable
                    && (
                    <WidgetPersonalAccountNotification
                        className="mb-3 unpaid-link unpaid-link_date"
                        data-btn="openUnpaidBillsDate"
                        icon={icons.infoDangerIcon}
                        text={stringFormat(translation.notificationPayDate, formatDate(nearestPayDate))}
                    >
                        <LinkBlock
                            className=" unpaid-link_date"
                            dataName="openUnpaidBillsDate"
                            url={getUnpaidBillsUrl()}
                            onClick={openUnpaidBills}
                        >
                            {translation.linkShow}
                        </LinkBlock>
                    </WidgetPersonalAccountNotification>
                )}
                {isCurrentBalanceDanger()
                    && outflowType === EAccountType.CREDIT
                    && isCustomRoleCheckAvailable
                    && (
                    <WidgetPersonalAccountNotification
                        className="widget-personal-account__credit-notification_danger"
                        icon={icons.infoDangerIcon}
                        text={translation.notificationCreditLimit}
                    />
                )}
                {!unpaidBillsAmount
                    && !unpaidBillsQuantity
                    && !isCurrentBalanceDanger()
                    && isCustomRoleCheckAvailable
                    && (
                    <WidgetPersonalAccountNotification
                        className="widget-personal-account__credit-notification_success"
                        icon={icons.likeGreenIcon}
                        text={translation.notificationGreat}
                    />
                )}
                {outflowType !== EAccountType.PREPAID && hasNewBills && showNewBillsNotification && (
                    <Fragment>
                        <SeparateLineStyled className="mt-4" />
                        <FlexMiddleStyled className="widget-personal-account__prepay-new-bills-text mt-3">
                            {translation.hasNewBillsText}
                        </FlexMiddleStyled>
                    </Fragment>
                )}
            </LayoutStyled>

            {outflowType === EAccountType.PREPAID
                && isCustomRoleCheckAvailable
                && (
                <Fragment>
                    <SeparateLineStyled className="mt-4" />

                    <FlexColumnStyled className="widget-personal-account__prepay-notification">
                        {hasNewBills && showNewBillsNotification && (
                            <FlexMiddleStyled className="widget-personal-account__prepay-new-bills-text mt-3">
                                {translation.hasNewBillsText}
                            </FlexMiddleStyled>
                        )}

                        <FlexMiddleStyled className="widget-personal-account__prepay-link mt-3 mb-3">
                            <LinkBlock dataName="redirectToBills" url={getBillsUrl()} onClick={onClickShowBillsLink}>
                                {translation.linkShowBills}
                            </LinkBlock>
                        </FlexMiddleStyled>
                        {creditValue === 0 && (
                            <FlexMiddleStyled className="widget-personal-account__bill-btn">
                                <Button
                                    className="widget-account__btn-bill"
                                    dataName="openMobileCash"
                                    kind="mediumNormal"
                                    onClick={openMobileCashHandler}
                                >
                                    {translation.btnBill}
                                </Button>
                            </FlexMiddleStyled>
                        )}
                    </FlexColumnStyled>
                </Fragment>
            )}

            {props.promisedPaymentButtonAvailable && (
                <LayoutStyled className="mt-3">
                    <FlexMiddleStyled>
                        <Button
                            className="widget-account__btn-promised-payment"
                            dataName="promisedPaymentButton"
                            kind="mediumNormal"
                            onClick={promisedPaymentButtonHandler}
                        >
                            {translation.promisedPayment.buttonLabel}
                        </Button>
                    </FlexMiddleStyled>
                </LayoutStyled>
            )}
        </FlexColumnStyled>
    );
};

export default WidgetPersonalAccountSuccess;
